import { useEffect, useState, useCallback } from 'react';
import { ApiError } from '@/lib/api.js';
import { getPhfSubmission, correctPhfField, getPhfDocuments, uploadPhfDocument, fetchSignedPhfPdf, getAnswerLists } from '@/services/pilots.service.js';
import { CHOICE_FIELDS } from '@/components/shared/AnswerEdit.jsx';
import { formatHumanDate } from '@/lib/date-display.js';
import { toast } from '@/components/ui/toaster.jsx';
import { Button } from '@/components/ui/button.jsx';
import { openDocument } from '@/lib/openDocument.js';

// THE PILOT HISTORY FORM, AS A FORM. Prompt 168, David's fifth ask:
// "it should be an editable form. period."
//
// Every prior pass improved the correction CONSOLE (a field dropdown, a value
// box, a reason box — the exact signature of correctPhfField rendered as a
// screen). This replaces it: the broker edits the number where the number is.
// Click a value → it becomes an input in place → a required "why?" line
// appears beneath THAT field → save goes through the same correction service.
// Nothing about the service changes: raw_submission stays immutable, the
// allowlist stays server-enforced (rows carry `field` only when the server
// says they are correctable), the audit trail and the corrected-value overlay
// are untouched.
//
// FULL PAGE, NOT A DRAWER. A form is a document; it gets the page, centered,
// scrolling normally at 100% zoom. The old Sheet anchored it left in a drawer,
// which David read at 50% zoom and still couldn't use.

function SectionHead({ children }) {
  return (
    <h3 className="rounded-t bg-[#1a3a4a] px-3 py-1.5 text-[12px] font-semibold uppercase tracking-[0.04em] text-white">
      {children}
    </h3>
  );
}

const isWide = (r) => String(r?.value ?? '').length > 55 || String(r?.label ?? '').length > 36;

function packRows(rows) {
  // Rows sharing a server-assigned `line` id pair on one grid row (up to
  // three: first/middle/last); a row without one takes a line alone unless
  // the old content-length heuristic pairs it. A row must pair fields that
  // belong together or not at all (prompt 169 SS1): the eye reads across
  // before it reads down.
  const out = [];
  let group = [];
  let groupId = null;
  const flush = () => { if (group.length) { out.push(group); group = []; groupId = null; } };
  const anyLines = rows.some((r) => r.line);
  for (const r of rows) {
    if (r.line) {
      if (groupId !== r.line || group.length >= 3) flush();
      groupId = r.line;
      group.push(r);
      continue;
    }
    flush();
    if (anyLines || isWide(r)) { out.push([r]); continue; }
    // Heuristic pairing only in sections with no line plan at all.
    const last = out[out.length - 1];
    if (last && last.length === 1 && !last[0].line && !isWide(last[0]) && !last[0]._paired) {
      last.push(r); last[0]._paired = true;
    } else {
      out.push([r]);
    }
  }
  flush();
  return out;
}

function FormGrid({ rows, phfId, editing, onStartEdit, onDone, dashed = false }) {
  if (!rows?.length) return null;
  return (
    <div className={`rounded-b border border-t-0 ${dashed ? 'border-dashed border-border' : 'border-border'}`}>
      {packRows(rows).map((line, i) => (
        <div
          key={i}
          className={`grid border-b border-border/50 last:border-b-0 ${line.length === 3 ? 'sm:grid-cols-3' : line.length === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}
        >
          {line.map((r) => (
            <FieldCell
              key={r.label}
              row={r}
              phfId={phfId}
              editing={editing}
              onStartEdit={onStartEdit}
              onDone={onDone}
              dashed={dashed}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// WHICH BLANK (utils/phf-submission-view.js offerUnanswered, lane C review
// C-R3). Only left_blank is the pilot leaving a question empty; the others say
// where the answer is, or that there is none yet.
const BLANK_WORDS = {
  left_blank: 'not answered on the form',
  not_on_form: 'not asked on this form',
  not_submitted: 'not submitted yet',
  not_entered: 'not entered',
  not_migrated: 'not migrated; on the CRM Contact',
};

// One field, editable in place when the server marked it correctable.
function FieldCell({ row, phfId, editing, onStartEdit, onDone, dashed }) {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [choices, setChoices] = useState(null);
  const editable = !!row.field;
  const isEditing = editing === row.field && editable;

  useEffect(() => {
    if (isEditing) { setValue(String(row.value ?? '')); setReason(''); }
    if (isEditing && CHOICE_FIELDS.has(row.field) && !choices) getAnswerLists().then((l) => setChoices(l[row.field] || [])).catch(() => toast.error('The list for this answer could not be read; nothing was changed'));
  }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    if (saving) return;
    // A reason is optional, never required (David 2026-11-04).
    // 1424.1512 was one keystroke from being saved as a corrected total with
    // a reason attached (prompt 169 SS3): the correction door exists to fix a
    // pilot's typo and must not make a worse one. Hours must parse as a
    // number before they go anywhere near the store.
    if (/_hours$/.test(row.field) && !Number.isFinite(Number(String(value).trim()))) {
      toast.error(`"${String(value).trim()}" is not a number. Check the value before saving.`);
      return;
    }
    if (row.field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) {
      toast.error(`"${String(value).trim()}" is not an email address. Check the value before saving.`);
      return;
    }
    setSaving(true);
    try {
      const r = await correctPhfField(phfId, { field: row.field, value: String(value).trim(), reason: reason.trim() || null });
      toast.success(r?.unchanged ? 'That is already the value on file' : 'Edit saved');
      // A pilot email is mirrored to the CRM Contact; a refusal there is
      // reported beside the recorded correction it does not undo.
      if (r?.mirror && r.mirror.applied === false) {
        toast.error(`Recorded in TRACON, but the CRM contact was not updated: ${r.mirror.error || r.mirror.code}`);
      }
      onDone(true);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not save the edit');
    } finally { setSaving(false); }
  }

  return (
    <div className="px-3 py-1.5 sm:border-r sm:border-border/40 sm:last:border-r-0">
      <div className="flex items-baseline gap-2">
        <span className="shrink-0 text-[11.5px] font-medium uppercase tracking-wide text-text-dim">
          {row.label}
        </span>
        {!isEditing ? (
          <span
            role={editable ? 'button' : undefined}
            tabIndex={editable ? 0 : undefined}
            onClick={editable ? () => onStartEdit(row.field) : undefined}
            onKeyDown={editable ? (e) => { if (e.key === 'Enter') onStartEdit(row.field); } : undefined}
            title={editable ? 'Click to edit this value' : undefined}
            className={`group min-w-0 flex-1 border-b border-dotted border-border pb-px text-[14px] ${dashed ? 'text-text-muted' : 'text-text'} ${editable ? 'cursor-text rounded-sm hover:bg-accent-soft/40' : ''}`}
          >
            {row.value === '' ? <span className="text-text-dim" data-unanswered={row.unanswered ? 'true' : undefined} data-blank={row.blank || undefined}>{BLANK_WORDS[row.blank] || '—'}</span> : row.value}
            {editable ? (
              <span className="ml-1.5 hidden text-[10.5px] text-text-dim group-hover:inline">edit</span>
            ) : null}
            {row.derived_from ? (
              <span className="ml-1.5 text-[10.5px] italic text-text-dim">derived from {row.derived_from}</span>
            ) : null}
            {row.correction ? (
              <span className="ml-2 text-[11px] font-medium text-warning" title={row.correction.reason || undefined}>
                broker corrected
                {row.correction.pilot_typed != null && row.correction.pilot_typed !== ''
                  ? <>, pilot typed <span className="whitespace-nowrap">{row.correction.pilot_typed}</span></>
                  : ', pilot left this blank'}
              </span>
            ) : null}
            {row.superseded_correction ? (
              <span className="ml-2 text-[11px] text-text-dim" data-superseded-correction>
                broker correction to {row.superseded_correction.value || 'blank'} was replaced by a later write
              </span>
            ) : null}
          </span>
        ) : CHOICE_FIELDS.has(row.field) ? (
          // A fixed answer is a pick from its one list (GET /api/tracon/answer-lists), never free text; a stored value
          // off the list is shown as it is to be replaced, never guessed onto it (David 2026-11-04).
          <select
            autoFocus
            value={choices && !choices.some((o) => o.value === value) ? '__off__' : value}
            disabled={!choices}
            onChange={(e) => { if (e.target.value !== '__off__') setValue(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Escape') onDone(false); if (e.key === 'Enter') save(); }}
            className="min-w-0 flex-1 rounded border border-accent bg-bg px-1.5 py-0.5 text-[14px] text-text"
          >
            {choices && !choices.some((o) => o.value === value) ? <option value="__off__" disabled>{value ? `${value} (not on the list: pick the right one)` : 'Choose'}</option> : null}
            {(choices || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input
            autoFocus
            value={value}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onDone(false); if (e.key === 'Enter') save(); }}
            className="min-w-0 flex-1 rounded border border-accent bg-bg px-1.5 py-0.5 text-[14px] text-text"
          />
        )}
      </div>
      {isEditing ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-1">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onDone(false); }}
            placeholder="Why it changed, e.g. logbook shows 512"
            className="min-w-[240px] flex-1 rounded border border-warning/60 bg-bg px-2 py-1 text-[12px] text-text placeholder:text-text-dim"
          />
          <Button size="sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save edit'}</Button>
          <Button size="sm" variant="ghost" disabled={saving} onClick={() => onDone(false)}>Cancel</Button>
        </div>
      ) : null}
    </div>
  );
}

// All-clear collapses to a COUNT and a toggle (David 2026-08-13): naming
// seventeen questions in a run-on line makes the eye process them anyway.
// Exceptions render as full rows above; the list stays one click away.
function AllClearLine({ count, label, rows, phfId, editing, onStartEdit, onDone, topBorder }) {
  const [show, setShow] = useState(false);
  return (
    <div className={`rounded-b border border-t-0 border-border px-3 py-1.5 ${topBorder ? 'border-t border-dashed' : ''}`}>
      <p className="text-[13px] text-text">
        <span className="font-medium">{label}</span>
        <span className="ml-2 text-text-muted">All {count} question{count === 1 ? '' : 's'} answered No.</span>
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="ml-2 text-[11.5px] text-text-dim underline hover:text-accent"
        >
          {show ? 'hide them' : 'see them'}
        </button>
      </p>
      {show ? (
        <div className="mt-1.5">
          <FormGrid rows={rows} phfId={phfId} editing={editing} onStartEdit={onStartEdit} onDone={onDone} dashed />
        </div>
      ) : null}
    </div>
  );
}

// ── Documents filed against this pilot ──────────────────────────────────
// The PHF's WorkDrive folder has held certificates since the form first took
// uploads; what it never had was a door from TRACON (prompt 121 built the
// service, nothing called it). Hargis 2026-09-04: a WINGS certificate emailed
// separately and used for a Starr discount had nowhere to go. This is the
// handle. The list spans every term (by contact), so next year's renewal sees
// this year's certificate; the upload files into THIS PHF's folder.
const DOC_TYPE_LABELS = {
  training_certificate: 'Training certificate',
  flight_review: 'Flight review',
  medical_certificate: 'Medical certificate',
  pilot_certificate: 'Pilot certificate',
  logbook_excerpt: 'Logbook excerpt',
  other: 'Other',
};

function PilotDocuments({ phfId, attach, onToggleAttach }) {
  const [docs, setDocs] = useState(null);
  const [types, setTypes] = useState(Object.keys(DOC_TYPE_LABELS));
  const [scope, setScope] = useState('phf');
  const [docsError, setDocsError] = useState('');
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [docType, setDocType] = useState('training_certificate');
  const [uploading, setUploading] = useState(false);

  const loadDocs = useCallback(() => {
    return getPhfDocuments(phfId)
      .then((r) => {
        setDocs(r?.documents || []);
        if (Array.isArray(r?.types) && r.types.length) setTypes(r.types);
        setScope(r?.scope || 'phf');
        setDocsError('');
      })
      .catch((err) => setDocsError(err instanceof ApiError ? (err.body?.error || err.code) : err.message));
  }, [phfId]);

  useEffect(() => { if (phfId) loadDocs(); }, [phfId, loadDocs]);

  async function upload() {
    if (uploading) return;
    if (!file) { toast.error('Choose a file first'); return; }
    if (!docType) { toast.error('Say what the document is'); return; }
    setUploading(true);
    try {
      const name = file.name;
      await uploadPhfDocument(phfId, file, docType);
      setFile(null);
      setInputKey((k) => k + 1);
      // The toast is a claim; the list re-read is the verification.
      await loadDocs();
      toast.success(`Filed ${name} to the pilot's folder`);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally { setUploading(false); }
  }

  return (
    <section>
      <SectionHead>Documents</SectionHead>
      <div className="rounded-b border border-t-0 border-border">
        {docsError ? (
          <p className="px-3 py-1.5 text-[12.5px] text-danger">Could not read the documents for this pilot: {docsError}</p>
        ) : docs === null ? (
          <p className="px-3 py-1.5 text-[12.5px] text-text-dim">Loading…</p>
        ) : docs.length === 0 ? (
          <p className="px-3 py-1.5 text-[12.5px] text-text-dim">Nothing filed for this pilot yet.</p>
        ) : (
          docs.map((d) => (
            <div key={d.id} className="flex flex-wrap items-baseline gap-x-2 border-b border-border/50 px-3 py-1.5 last:border-b-0">
              {/* Attach to the signed PHF: checked documents append after the form, in list order. */}
              <label className="flex items-center gap-1 text-[11.5px] text-text-muted" title="Attach to the signed PHF PDF">
                <input type="checkbox" checked={attach.has(String(d.id))} onChange={() => onToggleAttach(String(d.id))} disabled={!d.workdrive_file_id} />
                attach
              </label>
              {d.workdrive_permalink ? (
                <button type="button" onClick={() => openDocument(d.workdrive_permalink)} className="text-left text-[14px] font-medium text-accent underline-offset-2 hover:underline">
                  {d.filed_filename || d.filename}
                </button>
              ) : (
                <span className="text-[14px] font-medium text-text">{d.filed_filename || d.filename}</span>
              )}
              <span className="text-[11.5px] uppercase tracking-wide text-text-dim">
                {DOC_TYPE_LABELS[d.confirmed_type || d.document_type] || d.confirmed_type || d.document_type || 'unlabelled'}
              </span>
              <span className="text-[12px] text-text-muted">{formatHumanDate(d.filed_at || d.received_at) || ''}</span>
            </div>
          ))
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-dashed border-border px-3 py-2">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="rounded border border-border bg-bg px-2 py-1 text-[12.5px] text-text"
          >
            {types.map((t) => <option key={t} value={t}>{DOC_TYPE_LABELS[t] || t}</option>)}
          </select>
          <input
            key={inputKey}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="max-w-[280px] text-[12px] text-text-muted file:mr-2 file:rounded file:border file:border-border file:bg-bg file:px-2 file:py-0.5 file:text-[12px] file:text-text"
          />
          <Button size="sm" disabled={uploading || !file} onClick={upload}>{uploading ? 'Filing…' : 'File document'}</Button>
          <span className="text-[11px] text-text-dim">
            {scope === 'pilot' ? 'Every term, by contact. Files into this form’s folder.' : 'This form’s folder.'}
          </span>
        </div>
      </div>
    </section>
  );
}

const DISCLOSURES_TITLE = 'History and disclosures';
const MEDICAL_TITLE = 'Medical';
// The five medical rows that say No about one thing. A stated limitation
// (Scott MacKie's vision entry) is an exception and renders in full.
const MEDICAL_LIMITATION_LABELS = new Set([
  'Has limitations', 'Limitations', 'Vision', 'Colour vision', 'Color vision', 'Other limitation',
]);

function splitAllClear(rows, isCollapsible) {
  const clear = [];
  const exceptions = [];
  for (const r of rows || []) {
    if (isCollapsible(r) && r.value === 'No' && !r.correction) clear.push(r);
    else exceptions.push(r);
  }
  return { clear, exceptions };
}

export default function PhfSubmissionSheet({ phfId, pilotName, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(null); // correctable field currently in edit
  // The signed PHF (made on request): which filed documents ride along, and the fetch in flight.
  const [attach, setAttach] = useState(() => new Set());
  const [making, setMaking] = useState(false);
  const toggleAttach = (id) => setAttach((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function openSignedPdf() {
    if (making) return;
    setMaking(true);
    try {
      const { blob, standing, warnings } = await fetchSignedPhfPdf(phfId, [...attach]);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (standing === 'signed') toast.success(`Signed PHF opened${attach.size ? ` with ${attach.size} attachment${attach.size === 1 ? '' : 's'}` : ''}`);
      else toast.info(standing === 'stale'
        ? 'Opened, NOT signed: the pilot changed the form after signing it.'
        : standing === 'unknown' ? 'Opened, NOT marked signed: the signature record could not be read.' : 'Opened, NOT signed: the pilot has not signed this form.');
      for (const w of warnings) toast.info(w);
    } catch (err) {
      toast.error(`Could not make the signed PHF: ${err.message}`);
    } finally { setMaking(false); }
  }

  const reload = useCallback(() => {
    getPhfSubmission(phfId)
      .then((r) => setData(r?.submission || null))
      .catch((err) => setLoadError(err instanceof ApiError ? (err.body?.error || err.code) : err.message));
  }, [phfId]);

  useEffect(() => {
    if (!open || !phfId) return;
    setData(null);
    setLoadError('');
    setEditing(null);
    setAttach(new Set());
    reload();
  }, [open, phfId, reload]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !editing) onOpenChange(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, editing, onOpenChange]);

  if (!open) return null;

  const heading = data?.name || pilotName || 'Pilot';
  const onStartEdit = (field) => setEditing(field);
  const onDone = (savedIt) => {
    setEditing(null);
    // Re-read rather than patching local state: the form renders the server's
    // arrangement and derives nothing, so the corrected value must come back
    // the same way it arrived.
    if (savedIt) reload();
  };

  const gridProps = { phfId, editing, onStartEdit, onDone };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-bg">
      {/* STICKY: whose form this is stays on screen while the disclosures
          scroll — the wrong pilot's form must never be acted on. */}
      <div className="sticky top-0 z-10 border-b border-border bg-bg">
        <div className="mx-auto flex max-w-4xl items-start justify-between gap-3 px-6 py-3">
          <div>
            <h2 className="text-lg font-semibold text-text">{heading}, pilot history form</h2>
            <p className="text-[12.5px] text-text-muted">
              {[
                data?.aircraft?.length ? data.aircraft.map((a) => a.n_number).filter(Boolean).join(', ') : null,
                data?.submitted_at ? `Submitted ${String(data.submitted_at).slice(0, 10)}` : null,
              ].filter(Boolean).join(' · ')}
              {data?.aircraft?.length || data?.submitted_at ? '. ' : ''}
              {data?.submitted_at
                ? "These are the pilot's own answers, not the synced contact record."
                : "The pilot's own answers, not the synced contact record."}
              {' '}Click any value to edit it; a reason is optional, and the pilot&rsquo;s original
              stays on the record.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button type="button" size="sm" disabled={making || !data} onClick={openSignedPdf}>
              {making ? 'Making PDF…' : `Signed PHF (PDF)${attach.size ? ` + ${attach.size}` : ''}`}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 pb-16 pt-4">
        {loadError ? (
          <p className="mt-4 text-sm text-danger">{loadError}</p>
        ) : !data ? (
          <p className="mt-4 text-sm text-text-dim">Loading…</p>
        ) : (
          <div className="space-y-5">
            {data.source !== 'raw_submission' && data.reason ? (
              <div className="rounded border border-warning/40 bg-warning-soft/40 px-3 py-2">
                <p className="text-[12px] font-medium text-text">Full answers not yet in TRACON</p>
                <p className="mt-0.5 text-[11.5px] leading-snug text-text-muted">{data.reason}</p>
              </div>
            ) : null}

            {data.sections.map((s) => {
              if (s.title === DISCLOSURES_TITLE || s.title === MEDICAL_TITLE) {
                const collapsible = s.title === DISCLOSURES_TITLE
                  ? () => true
                  : (r) => MEDICAL_LIMITATION_LABELS.has(r.label);
                const { clear, exceptions } = splitAllClear(s.rows, collapsible);
                return (
                  <section key={s.title}>
                    <SectionHead>{s.title}</SectionHead>
                    {exceptions.length ? <FormGrid rows={exceptions} {...gridProps} /> : null}
                    {clear.length ? (
                      <AllClearLine
                        count={clear.length}
                        label={s.title === DISCLOSURES_TITLE ? 'No disclosures.' : 'No medical limitations.'}
                        rows={clear}
                        topBorder={exceptions.length > 0}
                        {...gridProps}
                      />
                    ) : null}
                    {!exceptions.length && !clear.length ? <FormGrid rows={s.rows} {...gridProps} /> : null}
                  </section>
                );
              }
              return (
                <section key={s.title}>
                  <SectionHead>{s.title}</SectionHead>
                  <FormGrid rows={s.rows} {...gridProps} />
                </section>
              );
            })}

            {data.aircraft?.length ? (
              <section>
                <SectionHead>Aircraft experience</SectionHead>
                <div className="rounded-b border border-t-0 border-border">
                  {data.aircraft.map((a, i) => (
                    <div key={`${a.n_number}-${i}`} className="border-b border-border/50 px-3 py-1.5 last:border-b-0">
                      <p className="text-[14px] font-semibold text-text">
                        {[a.n_number, a.display].filter(Boolean).join(' · ') || '—'}
                      </p>
                      <p className="text-[12.5px] text-text-muted">
                        {[
                          a.total_hours != null ? `${a.total_hours} total` : a.family_hours
                            ? `${a.family_hours.total != null ? `${a.family_hours.total} total` : ''}${a.family_hours.l12 != null ? ` · ${a.family_hours.l12} last 12 mo` : ''} (counted for the make and model)`
                            : 'hours not given',
                          a.total_hours != null && a.last_12 != null ? `${a.last_12} last 12 mo` : null,
                          a.requires_training === true ? 'training required' : null,
                          a.interest || null,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {data.unmapped?.length ? (
              <section>
                <SectionHead>Also answered</SectionHead>
                <FormGrid rows={data.unmapped} {...gridProps} dashed />
              </section>
            ) : null}

            {!data.sections.length && !data.aircraft?.length && !data.unmapped?.length ? (
              <p className="text-sm text-text-dim">This form carries no stored answers.</p>
            ) : null}

            <PilotDocuments phfId={phfId} attach={attach} onToggleAttach={toggleAttach} />
          </div>
        )}
      </div>
    </div>
  );
}
